import { ActionItemViewModel } from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { DefaultEditableActionItemContentCSS } from '../../../../models';
import { getDisplayName } from '../../../../models/NameUtils';
import { getDefaultDateStringValue } from '../../../../models/UiUtils';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { Button } from '../../../components-v2/Button';
import { InputFieldError } from '../../InputFieldError';
import { LoadingSpinner } from '../../LoadingSpinner';
import { VisibilityDropdown } from '../../VisibilityDropdown';
import { MentionEntitiesContext } from '../../mentions/MentionEntitiesContext';
import {
	IRichContentDocumentEditorConfig,
	RichContentDocumentEditor,
} from '../../richContent/RichContentDocumentEditor';
import { ActionItemCheckbox } from '../ActionItemCheckbox';
import { AssignDueDateButton } from '../AssignDueDateButton';
import { AssigneeButton } from '../AssigneeButton';
import { ActionItemEditorController } from './useActionItemEditor';
import { styleSheet } from './styles';

const DefaultEditorConfig: IRichContentDocumentEditorConfig = {
	autoresizeToFitContent: true,
	contentRawCss: DefaultEditableActionItemContentCSS,
	minHeight: 100,
	plugins: [],
	toolbar: false,
};

interface IProps {
	controller: ActionItemEditorController;
	actionItem: ActionItemViewModel;
	autoFocus?: boolean;
}

/**
 * The chrome-agnostic action-item field cluster — rich-text body, complete checkbox, due date,
 * assignee, visibility, and the mentioned-contacts panel — driven by {@link useActionItemEditor}.
 * It renders no header/footer/panel of its own, so any surface (the flyout editor today, and the
 * modal composer later) can wrap it in whatever chrome it needs. Observer-wrapped so the VM's
 * busy/saving state stays reactive.
 */
export const ActionItemEditorFields: React.FC<IProps> = observer(({ controller, actionItem, autoFocus }) => {
	const userSession = useUserSession();
	const checkboxId = React.useId();
	const {
		assignee,
		checked,
		dueDate,
		editorState,
		mentionedEntities,
		visibility,
		inputErrorMessage,
		setAssignee,
		setChecked,
		setDueDate,
		setMentionedEntities,
		setVisibility,
		onEditorStateChanged,
		clearError,
	} = controller;
	const disabled = actionItem.isSaving;

	return (
		<div className={css(styleSheet.fields)}>
			<section className={css(styleSheet.section)}>
				<span className={css(styleSheet.sectionLabel)}>Description</span>
				<InputFieldError errorMessage={inputErrorMessage}>
					<div className={css(styleSheet.editorRow)}>
						<ActionItemCheckbox
							checked={checked}
							disabled={disabled}
							id={checkboxId}
							onChange={e => setChecked(e.currentTarget.checked)}
						/>
						{editorState ? (
							<RichContentDocumentEditor
								autoFocus={autoFocus}
								className={css(styleSheet.editor_input)}
								config={DefaultEditorConfig}
								contentPlaceholderText='Type action item...'
								contentState={editorState}
								onContentStateChanged={onEditorStateChanged}
								onFocus={clearError}
							/>
						) : null}
					</div>
				</InputFieldError>
				{actionItem.isBusy ? (
					<div className={css(styleSheet.loading)}>
						<LoadingSpinner type='large' />
					</div>
				) : null}
			</section>

			<section className={css(styleSheet.section)}>
				<div className={css(styleSheet.detailRow)}>
					<span className={css(styleSheet.label)}>Due date</span>
					<div className={css(styleSheet.control)}>
						<AssignDueDateButton
							disabled={actionItem.isBusy}
							dueDate={dueDate ?? undefined}
							onDaySelected={setDueDate}
							readOnly={disabled}
						>
							<span className={css(dueDate ? styleSheet.controlValue : styleSheet.controlPlaceholder)}>
								{dueDate ? getDefaultDateStringValue(dueDate) : 'Set due date'}
							</span>
						</AssignDueDateButton>
						{dueDate ? (
							<Button
								variant='ghost'
								styles={[styleSheet.removeButton]}
								disabled={actionItem.isBusy}
								title='Remove due date'
								onClick={() => setDueDate(null)}
							>
								×
							</Button>
						) : null}
					</div>
				</div>

				<div className={css(styleSheet.detailRow)}>
					<span className={css(styleSheet.label)}>Assigned to</span>
					<div className={css(styleSheet.control)}>
						<AssigneeButton
							assignee={assignee ?? undefined}
							disabled={actionItem.isBusy}
							onAssigneeSelected={setAssignee}
							readOnly={disabled}
							userSession={userSession}
						>
							<span className={css(assignee ? styleSheet.controlValue : styleSheet.controlPlaceholder)}>
								{assignee ? getDisplayName(assignee) : 'Assign to…'}
							</span>
						</AssigneeButton>
						{assignee ? (
							<Button
								variant='ghost'
								styles={[styleSheet.removeButton]}
								disabled={actionItem.isBusy}
								title='Remove assignee'
								onClick={() => setAssignee(null)}
							>
								×
							</Button>
						) : null}
					</div>
				</div>

				<div className={css(styleSheet.detailRow)}>
					<span className={css(styleSheet.label)}>Visibility</span>
					<div className={css(styleSheet.control)}>
						<VisibilityDropdown disabled={disabled} onVisibilityChanged={setVisibility} visibility={visibility} />
					</div>
				</div>
			</section>

			<section className={css(styleSheet.section)}>
				<MentionEntitiesContext
					contextItemName='action item'
					mentionedEntities={mentionedEntities}
					onMentionedEntitiesChanged={setMentionedEntities}
				/>
			</section>
		</div>
	);
});
